import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LOGO_CONFIG, checkLogoExists } from './LogoConfig';

const Logo = ({ 
  variant = 'main', // 'main', 'white', 'square'
  size = 'navbar', // 'navbar', 'mobile', 'full'
  className = '',
  showText = true,
  animated = true
}) => {
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLogo = async () => {
      setIsLoading(true);
      
      // Determine which logo to try based on variant
      let logoPath;
      switch (variant) {
        case 'white':
          logoPath = LOGO_CONFIG.white;
          break;
        case 'square':
          logoPath = LOGO_CONFIG.square;
          break;
        case 'svg':
          logoPath = LOGO_CONFIG.svg;
          break;
        default:
          logoPath = LOGO_CONFIG.main;
      }

      // Check if the logo exists
      const logoExists = await checkLogoExists(logoPath);
      
      if (logoExists) {
        setLogoSrc(logoPath);
        setLogoError(false);
      } else {
        // Try SVG as fallback
        if (variant !== 'svg') {
          const svgExists = await checkLogoExists(LOGO_CONFIG.svg);
          if (svgExists) {
            setLogoSrc(LOGO_CONFIG.svg);
            setLogoError(false);
          } else {
            setLogoError(true);
          }
        } else {
          setLogoError(true);
        }
      }
      
      setIsLoading(false);
    };

    loadLogo();
  }, [variant]);

  // Get dimensions based on size
  const getDimensions = () => {
    switch (size) {
      case 'mobile':
        return LOGO_CONFIG.dimensions.mobile;
      case 'full':
        return LOGO_CONFIG.dimensions;
      case 'navbar':
      default:
        return LOGO_CONFIG.dimensions.navbar;
    }
  };

  const dimensions = getDimensions();

  // Loading state
  if (isLoading) {
    return (
      <div 
        className={`flex items-center space-x-2 ${className}`}
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <div className="w-8 h-8 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-xl animate-pulse" />
        {showText && (
          <div className="h-6 bg-gray-200 rounded animate-pulse flex-1" />
        )}
      </div>
    );
  }

  // Error state or no logo - show fallback
  if (logoError || !logoSrc) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <motion.div 
          className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-brand"
          animate={animated ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="text-white font-bold text-xl font-heading">D</span>
        </motion.div>
        {showText && (
          <div>
                         <div>
               <motion.span 
                 className="text-2xl font-bold font-heading text-visible-orange"
                 animate={animated ? { letterSpacing: ['0em', '0.05em', '0em'] } : {}}
                 transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
               >
                 DFITCLUB
               </motion.span>
               <p className="text-xs text-neutral-600 font-accent -mt-1">by Deeksha Rajput</p>
             </div>
          </div>
        )}
      </div>
    );
  }

  // Success state - show custom logo
  const logoHeightClass = size === 'navbar' ? 'h-14' : size === 'mobile' ? 'h-10' : 'h-16';
  const brandTextClass = size === 'navbar' ? 'text-lg' : size === 'mobile' ? 'text-sm' : 'text-xl';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <motion.img
        src={logoSrc}
        alt={LOGO_CONFIG.altText}
        className={`${logoHeightClass} w-auto object-contain`}
        animate={animated ? { scale: [1, 1.02, 1] } : {}}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        onError={() => setLogoError(true)}
      />
      {showText && variant !== 'square' && (
        <div className="leading-tight flex flex-col justify-center">
          <p className={`${brandTextClass} font-bold text-visible-orange font-heading tracking-wide`}>DFITCLUB</p>
          <p className="text-[10px] text-neutral-600 font-accent -mt-0.5">by Deeksha Rajput</p>
        </div>
      )}
    </div>
  );
};

export default Logo;